import type { IPickPageItem } from '~/modules/modal-pick-page';
import type { IVariantPriceData } from '../../../utils';
import type { IPriceProductRow } from '../types';

import {
  toPickPageItemFromVariantPrice,
  toVariantPriceRange,
  toVariantPriceStatus,
} from '../../../utils';
import { findVariantByOrigin, useCampaignContext } from '~/modules/campaign';
import { useCampaignPriceProductContext } from '../../../contexts';
import { PLATFORM_PRODUCT_PRICE_FIELD } from '../../../constants';
import { toPriceProductVariantRows } from '../utils';
import { useCallback, useMemo } from 'react';

export interface IUsePriceProductRowsResult {
  rows: IPriceProductRow[];
  /** The picks behind the rows, as the modal hands them over — what re-opening it starts from. */
  pickedProducts: IPickPageItem[];
  isLoadingRows: boolean;
  /** Takes the modal's whole selection, so a product left out of it drops off the table. */
  handlePickProducts: (items: IPickPageItem[]) => void;
  /** Drops one product off the table — selection and expansion follow, both being read off the rows. */
  handleRemoveProduct: (baseID: string) => void;
  /** Raw re-read; `usePriceProductSync` is what wraps it into the header's reportable action. */
  handleRefreshProducts: () => Promise<void>;
}

export const usePriceProductRows = (): IUsePriceProductRowsResult => {
  const { campaign, handleSetCampaignData } = useCampaignContext();
  const { products, isLoadingProducts, handleRefreshProducts } = useCampaignPriceProductContext();

  const controlVariant = findVariantByOrigin(campaign, true);
  const controlPrice = controlVariant?.price;

  // Read off the Control variant rather than copied into state of its own: the campaign is what the
  // save bar diffs and what discard restores, so the table picking a product turns the bar on for free.
  const pickedProducts = useMemo<IPickPageItem[]>(
    () => (controlPrice?.baseID ? [toPickPageItemFromVariantPrice(controlPrice)] : []),
    [controlPrice],
  );

  // The picked item covers the gap until its detail lands: it already carries a title and a thumbnail,
  // so a new row renders complete apart from its prices. The fetched product wins once it arrives,
  // since the merchant can rename or reprice it in Shopify after picking.
  const rows = useMemo(
    () =>
      pickedProducts.map((picked) => {
        const product = products?.find(({ baseID }) => baseID === picked.baseID);
        const title = product?.title ?? picked.title ?? '';
        const isDeleted = !!product?.isDeleted;
        // Unknown (still loading) reads as published, so a row never flashes an "Unpublished" badge
        // before its own detail has even landed.
        const isPublished = !product || !!(product.onlineStoreURL || product.publishedAt);

        return {
          baseID: picked.baseID,
          title,
          handle: product?.handle ?? picked.handle,
          image: product?.image ?? picked.image,
          variantCount: product?.variants?.length ?? 0,
          variants: toPriceProductVariantRows({
            baseID: picked.baseID,
            title,
            variants: product?.variants,
          }),
          // Gated on !product as well as the fetch flag: keepPreviousData means isLoadingProducts stays
          // true for a resolved row too while a sibling newly-picked row is what triggered the refetch.
          isLoadingDetail: isLoadingProducts && !product,
          status: isDeleted ? undefined : product?.status,
          isDeleted,
          isPublished,
          controlPrice: toVariantPriceRange({ variants: product?.variants }),
          controlComparePrice: toVariantPriceRange({
            variants: product?.variants,
            field: PLATFORM_PRODUCT_PRICE_FIELD.COMPARE_PRICE,
          }),
        };
      }),
    [pickedProducts, products, isLoadingProducts],
  );

  // Nothing reaches the server here: the campaign is only what the save bar diffs against, so writing
  // it is what turns the bar on and lets discard put the product back.
  const setControlPrice = useCallback(
    (price?: IVariantPriceData) => {
      if (!campaign || !controlVariant) return;

      // A Control re-pointed at another product makes the challenger's existing duplicate (if any)
      // unrelated to what is being tested now, so it's cleared back to the same "not yet duplicated"
      // state a brand new pick starts from — not left to be caught later only by a staleness check.
      // Its typed prices go with it: they name the old product's variants, none of which is on the
      // table any more.
      const isProductChanged = !!controlPrice?.baseID && controlPrice.baseID !== price?.baseID;
      const challengerVariant = isProductChanged ? findVariantByOrigin(campaign, false) : undefined;

      const variants = campaign.variants?.map((variant) => {
        if (variant?.id === controlVariant.id) return { ...variant, price };
        if (challengerVariant && variant?.id === challengerVariant.id) {
          return { ...variant, price: undefined, productVariants: [] };
        }

        return variant;
      });

      handleSetCampaignData({ ...campaign, variants });
    },
    [campaign, controlVariant, controlPrice, handleSetCampaignData],
  );

  // The picker is single-select, so the whole selection is one product — or none, once the merchant
  // unticks the one it opened on, which drops the row the same way removing it does.
  const handlePickProducts = useCallback(
    (items: IPickPageItem[]) => {
      const item = items[0];
      if (!item) return setControlPrice(undefined);

      setControlPrice({
        id: controlPrice?.id ?? '',
        baseID: item.baseID,
        handle: item.handle ?? '',
        title: item.title,
        status: toVariantPriceStatus(item.status) ?? 'UNLISTED',
        // Not a duplicate -- this is the Control's own real product, so it has no source to name.
        sourceProductBaseID: '',
      });
    },
    [controlPrice?.id, setControlPrice],
  );

  const handleRemoveProduct = useCallback(
    (baseID: string) => {
      if (controlPrice?.baseID !== baseID) return;

      setControlPrice(undefined);
    },
    [controlPrice?.baseID, setControlPrice],
  );

  return {
    rows,
    pickedProducts,
    isLoadingRows: isLoadingProducts,
    handlePickProducts,
    handleRemoveProduct,
    handleRefreshProducts,
  };
};
